/**
 * PlayAgainButton renders the "Play Again" button shown when a game ends.
 *
 * Why: This button has complex styling (pulsing glow, focus ring) and keyboard
 * handling. Extracting it keeps GameControls focused on layout.
 *
 * When to use: Rendered by GameControls when isGameOver is true.
 */
import * as React from 'react'
import { Button } from '@merels/ui'
import { cn } from '@merels/ui'
import { gameControlsTestIds } from './gameControls.testIds'

export interface PlayAgainButtonProps {
  /** Ref for focus management (callback or object ref) */
  buttonRef?: React.Ref<HTMLButtonElement>
  /** Click handler to restart the game */
  onClick: () => void
  /** Whether this button is currently focused */
  isFocused?: boolean
  /** Accent color for styling */
  accentColor?: string
  /** Background color for focus ring */
  backgroundColor?: string
  /** Button text (may include controller hint) */
  text: string
}

export function PlayAgainButton({
  buttonRef,
  onClick,
  isFocused,
  accentColor,
  backgroundColor,
  text,
}: PlayAgainButtonProps) {
  return (
    <Button
      ref={buttonRef}
      onClick={onClick}
      variant="default"
      size="sm"
      className={cn("animate-pulse-slow", isFocused && "outline-none")}
      data-testid={gameControlsTestIds.playAgainButton}
      style={accentColor ? {
        height: 'calc(var(--menu-bar-height,48px) * 0.66)',
        backgroundColor: accentColor,
        color: 'white',
        borderColor: accentColor,
        boxShadow: isFocused
          ? `0 0 0 2px ${accentColor}, 0 0 0 4px ${backgroundColor}, 0 0 12px ${accentColor}60`
          : `0 0 12px ${accentColor}60`
      } : undefined}
      tabIndex={isFocused ? 0 : -1}
    >
      {text}
    </Button>
  )
}
